import { SwimPose } from '../experience/swim';
import { restrictedRoom } from './politics';
/**
 * Other players, where you are. The game joins the room for the place you're in (director.roomKey()), tells it
 * where you are ~8 times a second, and draws everyone else in it as who they are: their own character, their
 * clothes, walking where they walk, dancing when they dance. Presence is automatic; nothing here interacts.
 *
 * Off unless there's a server: ?mp=ws://… on the URL, VITE_MP_URL in a build, or ws://localhost:8787 in dev.
 */
import * as THREE from 'three';
import { Npc } from '../human/npc';
import type { Appearance } from '../human/model';
import { socketFailed, socketOpened } from './health';
import { LIMITS, type ClientMsg, type PeerInfo, type Pose, type ServerMsg, type StageEvent, type StageKind } from './protocol';

export function mpUrl(): string | null {
  const q = new URLSearchParams(location.search).get('mp');
  if (q === 'off') return null;
  return q ?? (import.meta.env.VITE_MP_URL as string | undefined) ?? (import.meta.env.DEV ? 'ws://localhost:8787' : null);
}

/** who you are to the rooms: a random id kept on this device (?pid= for testing two lives in one browser) */
export function playerId(): string {
  // (?pid= sticks to the tab, like ?slot=: the creator → street hop drops the query)
  let q = new URLSearchParams(location.search).get('pid');
  try { if (q) sessionStorage.setItem('unfold.pid', q); else q = sessionStorage.getItem('unfold.pid'); } catch { /* storage blocked */ }
  if (q && /^[a-z0-9-]{8,64}$/i.test(q)) return q;
  try {
    let id = localStorage.getItem('unfold.playerId');
    if (!id) { id = crypto.randomUUID(); localStorage.setItem('unfold.playerId', id); }
    return id;
  } catch { return crypto.randomUUID(); }
}

/** a phone (a touch screen, a small one): the memory a browser tab gets there is a fraction of a laptop's */
const PHONE = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 900;
/** how many other players get a full body at once: the nearest ones (each is a MakeHuman with its own clothes and
 *  textures; a street full of new players built every one of them, and phones ran out of memory and reloaded) */
const BODIES = PHONE ? 5 : 10;
/** how often you tell the room where you are (s): 5 a second (8 was more than the easing needs, and every message
 *  goes to everyone in the room) */
const SEND_EVERY = 0.2;

interface Remote {
  info: PeerInfo;
  npc: Npc | null;
  loading: boolean;
  /** the latest pose from the network, and what's drawn (eased towards it) */
  want: Pose | null;
  emote: string | null;
  swim?: SwimPose | null;
  tag: HTMLDivElement;
  bars: HTMLElement[];
  ring: THREE.Mesh | null;
}

let css = false;
// a real player, not someone from the crowd: their name in a pill (the mic when theirs is on, bars when they speak),
// and a copper ring on the ground under them
const CSS = `.mp-tag { position:fixed; z-index:8; transform:translate(-50%,-100%); pointer-events:none; white-space:nowrap;
  display:flex; align-items:center; gap:6px; padding:4px 9px 4px 8px; border-radius:999px; background:rgba(30,22,16,.78);
  border:1px solid rgba(224,166,75,.85); box-shadow:0 2px 10px rgba(0,0,0,.45); color:#f3ead9; font:700 11.5px/1 "Segoe UI", system-ui, sans-serif; }
.mp-tag .mp-mic { display:none; width:12px; height:12px; }
.mp-tag.mic .mp-mic { display:block; }
.mp-tag .mp-bars { display:none; gap:2px; align-items:center; height:12px; }
.mp-tag.mic .mp-bars { display:inline-flex; }
.mp-tag .mp-bars b { width:2px; height:3px; border-radius:1px; background:#5fd068; }`;
const MIC = '<svg class="mp-mic" viewBox="0 0 24 24"><rect x="9" y="2" width="6" height="12" rx="3" fill="#5fd068"/><path d="M5 11a7 7 0 0 0 14 0M12 18v4" stroke="#5fd068" stroke-width="2" fill="none"/></svg>';
const RING = new THREE.RingGeometry(0.42, 0.5, 48).rotateX(-Math.PI / 2);
const RING_MAT = new THREE.MeshBasicMaterial({ color: 0xe0a64b, transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending });

export class Presence {
  /** set by the street: the pass for a government facility's room (the server refuses the room without it) */
  pass: (() => string | null) | null = null;
  private ws: WebSocket | null = null;
  private room: string | null = null;
  private peers = new Map<string, Remote>();
  private sendIn = 0;
  private last = '';
  private retry: ReturnType<typeof setTimeout> | null = null;
  /** the server's clock minus ours (ms): events here start at a server time */
  private skew = 0;
  /** when this room's night began (server ms): the music runs from it */
  epoch = 0;
  /** you're in a room now (the server's clock and tonight's start are known) */
  onRoom: (() => void) | null = null;
  /** their voice, for their tag: is their mic on, how loud are they speaking (0..1) */
  voiceOf: ((id: string) => { on: boolean; level: number }) | null = null;
  private beat = 0;
  /** something on the venue's stage, for everyone here (yours too) */
  onStage: ((ev: StageEvent) => void) | null = null;
  /** someone else's effect (money sprayed) */
  onFx: ((from: string, fx: { at: [number, number, number]; dir: [number, number]; n: number }) => void) | null = null;
  /** the server's clock, now (ms) */
  serverNow() { return Date.now() + this.skew; }
  /** ask for a slot on the stage (false: no room to ask) */
  stage(kind: StageKind, name: string, over: string): boolean { if (!this.connected) return false; this.post({ t: 'stage', kind, name, over }); return true; }
  /** an effect everyone here sees */
  fx(at: [number, number, number], dir: [number, number], n: number) { this.post({ t: 'fx', kind: 'spray', at, dir, n }); }
  /** someone came into the room (your mic's on: they should know) */
  onJoin: ((id: string) => void) | null = null;
  /** someone left the room (a date with them ends) */
  onLeave: ((id: string) => void) | null = null;
  /** players whose bodies a scene is staging here (the network's poses wait till it's done) */
  private held = new Set<string>();
  /** a message from another player in this room (requests, answers, session steps) */
  onMessage: ((from: string, data: unknown) => void) | null = null;

  constructor(private o: {
    url: string; id: string;
    name: () => string; look: () => Appearance | null;
    scene: THREE.Scene; renderer: THREE.WebGLRenderer; camera: THREE.Camera;
  }) {
    if (!css) { css = true; const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st); }
  }

  /** the players here now (for the interaction layer: who's near, who to ask) */
  /** how many other players have a body drawn right now */
  get bodies() { let n = 0; for (const r of this.peers.values()) if (r.npc) n++; return n; }
  get here(): { id: string; name: string; pos: THREE.Vector3 | null }[] {
    // (where they are: their body if it's drawn, else where the network says: a phone draws only the nearest few, and a
    // voice was silent for everyone without a body, or before theirs had loaded)
    return [...this.peers.values()].map((r) => ({ id: r.info.id, name: r.info.name, pos: r.npc && r.npc.root.visible ? r.npc.root.position : r.want && !r.want.hidden ? new THREE.Vector3(r.want.x, r.want.y, r.want.z) : null }));
  }
  /** other players' bodies near a point (negative ids: not the crowd's), for stepping round them */
  near(x: number, z: number, reach: number): { id: number; x: number; z: number }[] {
    const out: { id: number; x: number; z: number }[] = [];
    let i = 0;
    for (const r of this.peers.values()) {
      i++;
      const p = r.npc?.root;
      if (!p || !p.visible || Math.hypot(p.position.x - x, p.position.z - z) > reach) continue;
      out.push({ id: -i, x: p.position.x, z: p.position.z });
    }
    return out;
  }
  /**
   * Arrived right on top of someone (the same door, the same spot): one of you steps aside, the one whose id sorts
   * later, so you never both step the same way. Returns where to stand, or null.
   */
  stepAside(x: number, z: number, yaw: number): { x: number; z: number } | null {
    for (const r of this.peers.values()) {
      const p = r.npc?.root;
      if (!p || !p.visible || Math.hypot(p.position.x - x, p.position.z - z) > 0.4 || r.info.id > this.o.id) continue;
      return { x: x + Math.sin(yaw + Math.PI / 2) * 0.75, z: z + Math.cos(yaw + Math.PI / 2) * 0.75 };
    }
    return null;
  }
  /** their body here (for a scene that stages both of you) */
  body(id: string): Npc | null { return this.peers.get(id)?.npc ?? null; }
  /** everyone you've been in a room with this visit: their character, as they sent it (a friend at the wheel of the car you're in) */
  private looks = new Map<string, unknown>();
  lookOf(id: string): unknown | null { return this.peers.get(id)?.info.look ?? this.looks.get(id) ?? null; }
  /** the cars the others here have parked on this road */
  /** where the players here are sitting down (their seats are taken) */
  sitting(): THREE.Vector3[] { return [...this.peers.values()].filter((r) => r.want && !r.want.hidden && /sit/.test(r.want.emote ?? '')).map((r) => new THREE.Vector3(r.want!.x, r.want!.y, r.want!.z)); }
  cars(): { id: string; name: string; car: NonNullable<Pose['car']> }[] {
    return [...this.peers.values()].filter((r) => r.want?.car).map((r) => ({ id: r.info.id, name: r.info.name, car: r.want!.car! }));
  }
  /** their latest pose (null: not here, or not heard from yet) */
  poseOf(id: string): Pose | null { return this.peers.get(id)?.want ?? null; }
  /** they're in this room */
  has(id: string) { return this.peers.has(id); }
  /** hold their body still for a scene (true), or hand it back to the network (false) */
  hold(id: string, on: boolean) { if (on) this.held.add(id); else this.held.delete(id); }
  get connected() { return this.ws?.readyState === WebSocket.OPEN; }
  get roomKey() { return this.room; }

  /** be in this room (null: in none). Changing rooms drops everyone from the last one. */
  setRoom(key: string | null) {
    if (key === this.room) return;
    this.close();
    this.room = key;
    if (key) this.open();
  }

  private open() {
    const key = this.room;
    if (!key) return;
    let ws: WebSocket;
    const pass = restrictedRoom(key) ? this.pass?.() ?? '' : '';
    try { ws = new WebSocket(`${this.o.url.replace(/\/$/, '')}/room/${encodeURIComponent(key)}${pass ? `?pass=${encodeURIComponent(pass)}` : ''}`); } catch { return; }
    this.ws = ws;
    let opened = false;
    ws.onopen = () => {
      opened = true; socketOpened();
      const look = this.o.look();
      this.post({ t: 'hello', id: this.o.id, name: this.o.name().slice(0, LIMITS.name), look: look && JSON.stringify(look).length <= LIMITS.look ? look : null });
      this.last = '';
    };
    ws.onmessage = (e) => { if (this.ws !== ws) return; try { this.receive(JSON.parse(String(e.data)) as ServerMsg); } catch { /* not ours */ } }; // (a room you've just left still talking: not this room's people)
    ws.onclose = () => {
      if (this.ws !== ws) return;
      if (!opened) socketFailed(); // (never got through: evidence for the connection check; a room changed isn't)
      this.ws = null;
      this.clearPeers();
      // (the room went away under us: try again while we're still meant to be in it)
      if (this.room === key) this.retry = setTimeout(() => { this.retry = null; if (this.room === key && !this.ws) this.open(); }, 3000);
    };
  }

  private close() {
    if (this.retry) { clearTimeout(this.retry); this.retry = null; }
    const ws = this.ws;
    this.ws = null;
    try { ws?.close(); } catch { /* already */ }
    this.clearPeers();
  }

  private post(m: ClientMsg) { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m)); }

  /** a message to one player in this room (false: they're not here) */
  sendTo(id: string, data: unknown): boolean {
    if (!this.peers.has(id) || !this.connected) return false;
    this.post({ t: 'to', id, data });
    return true;
  }

  private receive(m: ServerMsg) {
    switch (m.t) {
      case 'peers': this.skew = m.now - Date.now(); this.epoch = m.epoch; this.clearPeers(); for (const p of m.peers) { this.add(p); this.looks.set(p.id, p.look); } this.onRoom?.(); break;
      case 'stage': this.skew = m.now - Date.now(); this.onStage?.(m.ev); break;
      case 'fx': if (this.peers.has(m.id)) this.onFx?.(m.id, m); break;
      case 'join': this.add(m.peer); this.looks.set(m.peer.id, m.peer.look); break;
      case 'leave': this.drop(m.id); break;
      case 'pose': { const r = this.peers.get(m.id); if (r) r.want = m.pose; break; }
      case 'from': if (this.peers.has(m.id)) this.onMessage?.(m.id, m.data); break;
      case 'full': this.room = null; break;
    }
  }

  private add(info: PeerInfo) {
    if (info.id === this.o.id) return;
    this.drop(info.id);
    const tag = document.createElement('div'); tag.className = 'mp-tag'; tag.hidden = true;
    tag.innerHTML = `${MIC}<span></span><span class="mp-bars"><b></b><b></b><b></b></span>`;
    tag.querySelector('span')!.textContent = info.name;
    document.body.appendChild(tag);
    this.peers.set(info.id, { info, npc: null, loading: false, want: info.pose, emote: null, tag, bars: [...tag.querySelectorAll<HTMLElement>('.mp-bars b')], ring: null });
    this.onJoin?.(info.id);
  }

  private drop(id: string) {
    const r = this.peers.get(id);
    if (!r) return;
    this.peers.delete(id);
    r.npc?.dispose();
    r.tag.remove();
    this.held.delete(id);
    this.onLeave?.(id);
  }

  private clearPeers() { for (const id of [...this.peers.keys()]) this.drop(id); }

  /** their body (built once, when there's room for another) */
  /** who gets a body: the nearest BODIES players you can see (checked every second; a body far off, when someone
   *  nearer is waiting for one, is given up) */
  private bodied = new Set<string>();
  private nearIn = 0;
  private choose(me: Pose | null) {
    const at = me ?? this.o.camera.position;
    const seen = [...this.peers.values()].filter((r) => r.want && !r.want.hidden).sort((a, b) => Math.hypot(a.want!.x - at.x, a.want!.z - at.z) - Math.hypot(b.want!.x - at.x, b.want!.z - at.z));
    this.bodied = new Set(seen.slice(0, BODIES).map((r) => r.info.id));
    for (const r of this.peers.values()) {
      if (r.npc && !this.bodied.has(r.info.id) && !this.held.has(r.info.id)) { r.npc.dispose(); r.npc = null; r.ring = null; r.emote = null; r.tag.hidden = true; }
    }
  }

  private build(r: Remote) {
    if (r.loading || r.npc) return;
    if (!this.bodied.has(r.info.id) && !this.held.has(r.info.id)) return;
    if ([...this.peers.values()].filter((x) => x.npc || x.loading).length >= BODIES) return;
    r.loading = true;
    const look = (r.info.look ?? {}) as Partial<Appearance>;
    Npc.create(look, this.o.renderer, { lite: true }).then((npc) => { // (light: other players' pictures shared and smaller)
      if (this.peers.get(r.info.id) !== r) { npc.dispose(); return; }
      r.npc = npc; r.loading = false;
      r.ring = new THREE.Mesh(RING, RING_MAT); r.ring.position.y = 0.03; r.ring.renderOrder = 2; npc.root.add(r.ring);
      npc.stride = { move: 0, run: 0 };
      if (r.want) { npc.root.position.set(r.want.x, r.want.y, r.want.z); npc.root.rotation.y = r.want.yaw; }
      this.o.scene.add(npc.root);
    }).catch(() => { r.loading = false; });
  }

  /** each frame: tell the room where you are (8 Hz, only when it changed), move everyone else */
  update(dt: number, me: Pose | null) {
    this.sendIn -= dt;
    if (me && this.sendIn <= 0 && this.connected) {
      this.sendIn = SEND_EVERY;
      const r = (n: number) => Math.round(n * 100) / 100;
      const pose: Pose = { x: r(me.x), y: r(me.y), z: r(me.z), yaw: r(me.yaw), move: r(me.move), run: r(me.run), emote: me.emote, ...(me.hidden ? { hidden: true } : {}), ...(me.car ? { car: { m: me.car.m, c: me.car.c, x: r(me.car.x), z: r(me.car.z), yaw: r(me.car.yaw), ...(me.car.v ? { v: 1 as const } : {}), ...(me.car.f?.length ? { f: me.car.f } : {}) } } : {}) };
      const key = JSON.stringify(pose);
      if (key !== this.last) { this.last = key; this.post({ t: 'pose', pose }); }
    }
    this.nearIn -= dt;
    if (this.nearIn <= 0) { this.nearIn = 1; this.choose(me); }
    const cam = this.o.camera;
    for (const r of this.peers.values()) {
      if (!r.want || r.want.hidden || this.drawnElsewhere?.(r.info.id)) { if (r.npc) r.npc.root.visible = false; r.tag.hidden = true; continue; } // (one of them, ever: hidden, or drawn by the game itself)
      if (!r.npc) { this.build(r); r.tag.hidden = true; continue; }
      const n = r.npc, w = r.want, root = n.root;
      root.visible = true;
      if (this.held.has(r.info.id)) { n.update(dt); this.afterHeld?.(r.info.id); r.tag.hidden = true; continue; } // (a scene has them)
      // on a ride: in their seat on our copy of it (sat down), not eased about
      if (this.onRide?.(w.emote, root)) { if (r.emote !== w.emote) { r.emote = w.emote; void n.play('sit', 0, false); } n.update(dt); r.tag.hidden = true; continue; }
      // ease towards where they are (a jump if they've teleported: a door, a seat)
      const far = Math.hypot(w.x - root.position.x, w.z - root.position.z) > 6;
      const k = far ? 1 : 1 - Math.exp(-dt * 10);
      root.position.x += (w.x - root.position.x) * k; root.position.y += (w.y - root.position.y) * k; root.position.z += (w.z - root.position.z) * k;
      let d = w.yaw - root.rotation.y; d = Math.atan2(Math.sin(d), Math.cos(d));
      root.rotation.y += d * (far ? 1 : 1 - Math.exp(-dt * 12));
      n.stride = { move: w.move, run: w.run };
      if (w.emote !== r.emote) { r.emote = w.emote; void n.play(w.emote === 'swim' ? null : w.emote?.startsWith('ride:') ? 'sit' : w.emote, 0, false); }
      n.update(dt);
      // (swimming: the same stroke as on their own screen, from how fast they're moving; they used to stand in the water)
      if (w.emote === 'swim') { r.swim ??= new SwimPose(root); if (r.swim.ok) { r.swim.apply(dt, Math.min(1, w.move + w.run * 0.5), w.y); root.position.y = w.y; } } else r.swim = null; // (their height already has the swim's lift in it)
      this.afterPeer?.(r.info.id, root, dt);
      // their name, up close
      const head = root.position.clone().setY(root.position.y + 2.0);
      const dist = head.distanceTo(cam.position), v = head.clone().project(cam);
      const ahead = head.clone().sub(cam.position).dot(cam.getWorldDirection(new THREE.Vector3())) > 0;
      r.tag.hidden = !(ahead && dist < 14 && Math.abs(v.x) < 1 && Math.abs(v.y) < 1);
      if (!r.tag.hidden) {
        r.tag.style.left = `${(v.x * 0.5 + 0.5) * innerWidth}px`; r.tag.style.top = `${(-v.y * 0.5 + 0.5) * innerHeight}px`;
        // the mic when theirs is on; the bars move with their voice
        const vs = this.voiceOf?.(r.info.id);
        r.tag.classList.toggle('mic', !!vs?.on);
        if (vs?.on) { this.beat += dt; r.bars.forEach((b, i) => { b.style.height = `${3 + vs.level * 9 * (0.6 + 0.4 * Math.abs(Math.sin(this.beat * 9 + i * 1.7)))}px`; }); }
      }
    }
  }

  /** a place can seat other players itself (a ride: they say which seat; the place knows where it is now): true = placed */
  onRide: ((emote: string | null, root: THREE.Object3D) => boolean) | null = null;
  /** a scene posing a held player, after their animation this frame */
  afterHeld: ((id: string) => void) | null = null;
  /** the game draws this player itself just now (in a seat of a car): their network body isn't drawn as well */
  drawnElsewhere: ((id: string) => boolean) | null = null;
  /** after a player's body is posed (the rink: on skates) */
  afterPeer: ((id: string, root: THREE.Object3D, dt: number) => void) | null = null;

  dispose() { this.room = null; this.close(); }
}
